import * as jsts from "jsts";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { CreateRibbon } from "@babylonjs/core/Meshes/Builders/ribbonBuilder";
import { ElementGeometry } from "./ElementGeometry";
import { RoomManager } from './../../RoomManager';
import { Util } from "../../utils/Util";
import { clamp } from "@egr/wcf/modules/utils/math";
import { newError } from '@egr/wcf/modules/utils/error';
export class Rectangle extends ElementGeometry {
    constructor(pRoomManager, pElement) {
        super(pRoomManager, pElement);
        this.mRoomElement = pElement;
        this.mRoomManager = pRoomManager;
        this.mPoints = new Array();
        this.mWidth = 0;
        this.mDepth = 0;
        this.mArea = 0;
        this.mCenter = Vector3.Zero();
        this.mminPoint = Vector3.Zero();
        this.mmaxPoint = Vector3.Zero();
        this.mHillPoints_Offset = null;
        this.mHillPoints_Base = null;
        this.mcanCreateHill = false;
        this.mRoomElement.Params.ElementPoints = new Array();
        this.mRoomElement.Params.ElementOriginalPoints = new Array();
        this.mRoomElement.Params.material = this.mRoomElement.material;
        this.mRoomElement.Params.materialColor = this.getInitialParams().color;
    }
    getType() {
        return ("Rectangle");
    }
    setMesh(pMesh) {
        this.mArea = Util.calcMeshArea(pMesh) * 0.25;
    }
    get drawnMesh2d() {
        return (this.mMesh2d);
    }
    get drawnMesh3d() {
        return (this.mMesh3d);
    }
    getPoints() {
        return this.mPoints;
    }
    getPolygon(pOffset = true) {
        let tRes = new Array();
        let tnodePos = new Vector3(0, 0, 0);
        if (pOffset)
            tnodePos = this.mRoomElement.position.clone();
        tRes.push(this.mPoints[0].add(tnodePos));
        tRes.push(this.mPoints[1].add(tnodePos));
        tRes.push(this.mPoints[2].add(tnodePos));
        tRes.push(this.mPoints[3].add(tnodePos));
        return (tRes);
    }
    get width() {
        return (this.mWidth);
    }
    get depth() {
        return this.mDepth;
    }
    center() {
        return this.mCenter;
    }
    get minPoint() {
        return (this.mminPoint);
    }
    get maxPoint() {
        return (this.mmaxPoint);
    }
    showInteractors(pShow) {
        if (this.mInteractors != null)
            this.mInteractors.showInteractors(pShow);
    }
    correctDirection() {
        let tClockwise = Util._clockWise3(this.mPoints);
        if (tClockwise)
            this.mPoints.reverse();
    }
    checkPointOrder() {
        let tmaxX = -Infinity;
        let tminY = Infinity;
        for (let tPoint of this.mPoints) {
            if (tPoint.x > tmaxX)
                tmaxX = tPoint.x;
            if (tPoint.z < tminY)
                tminY = tPoint.z;
        }
        if (Util.numbersEqual(this.mPoints[0].x, tmaxX, 0.01) &&
            Util.numbersEqual(this.mPoints[0].z, tminY, 0.01)) {
            let tPoints = [];
            for (let tPoint of this.mPoints) {
                tPoints.push(tPoint.clone());
            }
            this.mPoints = [];
            this.mPoints.push(tPoints[2]);
            this.mPoints.push(tPoints[3]);
            this.mPoints.push(tPoints[0]);
            this.mPoints.push(tPoints[1]);
        }
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.0;
        tParams.height = 0.0;
        tParams.floorOffset = 0.0;
        tParams.color = "#999999";
        return (tParams);
    }
    get Area() {
        return (this.mArea);
    }
    get Perimeter() {
        return (this.mPerimeter);
    }
    getparent() {
        return (this.mParent);
    }
    setNewWidth(pNewWidth) {
        let tLength = clamp(pNewWidth, 0.1, 500);
        let tREPoints = this.getPoints();
        let tsideLine = tREPoints[3].subtract(tREPoints[0]);
        tsideLine.normalize();
        tsideLine = tsideLine.scale(tLength);
        tREPoints[2] = tREPoints[1].add(tsideLine);
        tREPoints[3] = tREPoints[0].add(tsideLine);
        let tREnewPoints = new Array();
        tREnewPoints.push(tREPoints[0]);
        tREnewPoints.push(tREPoints[1]);
        tREnewPoints.push(tREPoints[2]);
        tREnewPoints.push(tREPoints[3]);
        this.setPoints(tREnewPoints);
    }
    setNewDepth(pNewDepth) {
        let tDepth = clamp(pNewDepth, 0.1, 500);
        let tREPoints = this.getPoints();
        let ttopLine = tREPoints[1].subtract(tREPoints[0]);
        ttopLine.normalize();
        ttopLine = ttopLine.scale(tDepth);
        tREPoints[1] = tREPoints[0].add(ttopLine);
        tREPoints[2] = tREPoints[3].add(ttopLine);
        let tREnewPoints = new Array();
        tREnewPoints.push(tREPoints[0]);
        tREnewPoints.push(tREPoints[1]);
        tREnewPoints.push(tREPoints[2]);
        tREnewPoints.push(tREPoints[3]);
        this.setPoints(tREnewPoints);
    }
    create2dGeometry(pParent) {
        this.mParent = pParent;
        this.calcSize();
        let tRectangle = this.createRectangle(pParent);
        if (tRectangle != null) {
            this.mRoomElement.setMesh2d(tRectangle);
            this.mRoomElement.Params.MeshVertices = tRectangle.getVerticesData(VertexBuffer.PositionKind);
            this.calcLenght();
        }
        let tHill = this.createHill(pParent);
        this.mergeShapeMeshes(pParent, this.mRoomElement.setMesh2d);
    }
    create3dGeometry(pParent) {
        let tRectangle = this.createRectangle(pParent);
        if (tRectangle != null)
            this.mRoomElement.setMesh3d(tRectangle);
        let tHill = this.createHill(pParent);
        this.mergeShapeMeshes(pParent, this.mRoomElement.setMesh3d);
    }
    createSpecialGeometry(pParent) {
        if (this.mRoomElement.crossHairVisible)
            this.createCrossHair(pParent);
    }
    getProperties() {
        let tProperties = new Array();
        return (tProperties);
    }
    serializeIOData() {
        return (this.mRoomElement.Params);
    }
    loadIOData(pData) {
        let tParams = pData;
        let tPoints = new Array();
        for (let tEP of tParams.ElementPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tEP.x;
            tP.z = tEP.z;
            tPoints.push(tP);
        }
        this.setPoints(tPoints);
    }
    setPoints(pPoints, pCreateCAG = false) {
        let tL1 = Vector3.Distance(pPoints[0], pPoints[1]);
        let tL2 = Vector3.Distance(pPoints[0], pPoints[3]);
        if (tL1 < 0.09 || tL2 < 0.091)
            return (true);
        this.mPoints = [];
        let tP1 = pPoints[0];
        let tP2 = pPoints[1];
        let tP3 = pPoints[2];
        let tP4 = pPoints[3];
        this.mPoints.push(tP1);
        this.mPoints.push(tP2);
        this.mPoints.push(tP3);
        this.mPoints.push(tP4);
        this.correctDirection();
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcSize();
        this.mRoomElement.setClosedPolygon(true);
        if (pCreateCAG) {
            let tPoints = new Array();
            for (let tP of this.mPoints) {
                tPoints.push(new CSG.Vector2D(tP.x, tP.z));
            }
            let tCAG = CAG.fromPointsNoCheck(tPoints);
            this.mRoomElement.setCAG(tCAG);
            return (true);
        }
        return (false);
    }
    get2dPoints() {
        let tPoints = new Array();
        tPoints.push(new Vector2(this.mRoomElement.Params.ElementPoints[0].x, this.mRoomElement.Params.ElementPoints[0].z));
        tPoints.push(new Vector2(this.mRoomElement.Params.ElementPoints[2].x, this.mRoomElement.Params.ElementPoints[2].z));
        tPoints.push(new Vector2(this.mRoomElement.Params.ElementPoints[3].x, this.mRoomElement.Params.ElementPoints[3].z));
        tPoints.push(new Vector2(this.mRoomElement.Params.ElementPoints[1].x, this.mRoomElement.Params.ElementPoints[1].z));
        return (tPoints);
    }
    calcLenght() {
        let tLines = new Array();
        let tP1 = this.mRoomElement.Params.ElementPoints[0];
        let tP2 = this.mRoomElement.Params.ElementPoints[1];
        let tP3 = this.mRoomElement.Params.ElementPoints[2];
        let tP4 = this.mRoomElement.Params.ElementPoints[3];
        tLines.push([tP1, tP2]);
        tLines.push([tP1, tP4]);
        tLines.push([tP4, tP3]);
        tLines.push([tP3, tP2]);
        let tDist = 0;
        for (let tLine of tLines) {
            tDist += Vector3.Distance(tLine[0], tLine[1]);
        }
        this.mPerimeter = tDist;
        this.calcArea(tLines);
    }
    calcArea(pLines) {
        let tSide1 = Vector3.Distance(pLines[0][0], pLines[0][1]);
        let tSide2 = Vector3.Distance(pLines[1][0], pLines[1][1]);
        let tarea = tSide1 * tSide2;
        this.mArea = tarea;
    }
    calcSize() {
        if (this.mPoints.length === 0)
            return;
        let tminX = this.mPoints[0].x;
        let tminY = this.mPoints[0].z;
        let tmaxX = this.mPoints[0].x;
        let tmaxY = this.mPoints[0].z;
        for (let tPoint of this.mPoints) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminY = Math.min(tPoint.z, tminY);
            tmaxY = Math.max(tPoint.z, tmaxY);
        }
        this.mWidth = Vector3.Distance(this.mRoomElement.Params.ElementPoints[0], this.mRoomElement.Params.ElementPoints[3]);
        this.mDepth = Vector3.Distance(this.mRoomElement.Params.ElementPoints[0], this.mRoomElement.Params.ElementPoints[1]);
        this.mRoomElement.setWidth(this.mWidth, false, false);
        this.mRoomElement.setDepth(this.mDepth, false, false);
        this.mminPoint = new Vector3(tminX, 0, tminY);
        this.mmaxPoint = new Vector3(tmaxX, 0, tmaxY);
        this.mCenter.x = tmaxX - (tmaxX - tminX) * 0.5;
        this.mCenter.z = tmaxY - (tmaxY - tminY) * 0.5;
    }
    getCorrectedPolyPath() {
        let tP = this.getPolygon(false);
        if (Util.vectorEquals3(tP[0], tP[tP.length - 1]))
            tP.pop();
        if ((tP.length > 3) && (Util.vectorEquals3(tP[tP.length - 1], tP[tP.length - 2]))) {
            tP.pop();
        }
        tP = Util.correctPolygonDirection3(tP);
        return (tP);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createOffsetCSG(pBasePoly) {
        let tRes = {};
        tRes.CAG = null;
        tRes.CSG = null;
        const tHillOffset = this.mRoomElement.Params.hillOffset;
        const tHillType = this.mRoomElement.Params.terrainType;
        if (tHillType === "Flat") {
            return (tRes);
        }
        let tbasePolygon = pBasePoly;
        let toffsetPoints = offset(tbasePolygon, (tHillType === 'Top' ? tHillOffset : -tHillOffset));
        if (toffsetPoints.length !== tbasePolygon.length) {
            return (tRes);
        }
        // offset plane
        let tPointsOffset = new Array();
        let tCAGOffset = null;
        toffsetPoints = Util.correctPolygonDirection3(toffsetPoints);
        this.limitOffsetDistance();
        if (tHillType === 'Bottom') {
            for (let tP1 of toffsetPoints) {
                tP1.addInPlace(this.mRoomElement.Params.hillTopOffset);
            }
        }
        if (toffsetPoints.length === tbasePolygon.length) {
            this.mHillPoints_Offset = toffsetPoints;
        }
        for (let tPoint of toffsetPoints) {
            tPointsOffset.push(new CSG.Vector2D(tPoint.x, tPoint.z));
        }
        if (toffsetPoints.length === tbasePolygon.length) {
            tCAGOffset = CAG.fromPointsNoCheck(tPointsOffset);
        }
        if (tCAGOffset == null || (tCAGOffset.isSelfIntersecting())) {
            return (tRes);
        }
        let tCSGOffset = Util.CAG2CSG(tCAGOffset);
        tRes.CAG = tCAGOffset;
        tRes.CSG = tCSGOffset;
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createCSG(pParent) {
        const tHillHeight = this.mRoomElement.Params.hillHeight;
        const tHillType = this.mRoomElement.Params.terrainType;
        let tbasePolygon = this.getCorrectedPolyPath();
        this.mHillPoints_Base = tbasePolygon;
        let tCAGCSGOffset = this.createOffsetCSG(tbasePolygon);
        if (tHillType === 'Top') {
            for (let tP1 of tbasePolygon) {
                tP1.addInPlace(this.mRoomElement.Params.hillTopOffset);
            }
        }
        // hill base plane / initial 2d shape
        let tPoints = new Array();
        for (let tPoint of tbasePolygon) {
            tPoints.push(new CSG.Vector2D(tPoint.x, tPoint.z));
        }
        let tCAGBase = CAG.fromPointsNoCheck(tPoints);
        if (tCAGBase.isSelfIntersecting()) {
            this.mRoomElement.setCAG(null);
            return (null);
        }
        this.mcanCreateHill = false;
        let tCSGBase = null;
        tCSGBase = Util.CAG2CSG(tCAGBase);
        this.mRoomElement.setCAG(tCAGBase);
        this.mRoomElement.setCAGoriginal(tCAGBase);
        if (tCAGCSGOffset.CAG != null && (!tCAGCSGOffset.CAG.isSelfIntersecting()) && (tHillType !== 'Flat')) {
            if (tCAGCSGOffset.CSG != null) {
                this.mcanCreateHill = true;
            }
            if (tHillType === "Top") {
                tCSGBase = tCSGBase.translate([0, tHillHeight, 0]);
                this.mRoomElement.setCAG(tCAGCSGOffset.CAG);
                this.mRoomElement.setCAGoriginal(tCAGCSGOffset.CAG);
            }
            if ((tHillType === "Bottom") && (tCAGCSGOffset.CSG != null)) {
                tCAGCSGOffset.CSG = tCAGCSGOffset.CSG.translate([0, tHillHeight, 0]);
                this.mcanCreateHill = true;
            }
        }
        let tMeshes_base = null;
        let tMeshes_offset = null;
        switch (tHillType) {
            case 'Flat':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'rectangle', tCSGBase, this.mRoomElement.localMaterial);
                if (tMeshes_base == null) {
                    return (null);
                }
                return (tMeshes_base[0]);
            case 'Bottom':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'rectangle', tCSGBase, this.mRoomElement.localMaterial);
                tMeshes_offset = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'rectangle', tCAGCSGOffset.CSG, this.mRoomElement.localMaterial);
                if (tMeshes_base == null) {
                    return (null);
                }
                return (tMeshes_base[0]);
            case 'Top':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'rectangle', tCSGBase, this.mRoomElement.localMaterial);
                tMeshes_offset = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'rectangle', tCAGCSGOffset.CSG, this.mRoomElement.localMaterial);
                if (tMeshes_offset == null) {
                    return (null);
                }
                return (tMeshes_offset[0]);
            default:
                break;
        }
        return (null);
    }
    /*
    *     tP1--------------------------tP2
    *      |                            |
    *      |                            |
    *      |                            |
    *     tP3--------------------------tP4
    */
    createRectangle(pParent) {
        if (this.mPoints.length === 0)
            return (null);
        let tMesh = this.createCSG(pParent);
        return (tMesh);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createHill(pParent) {
        const basePoints = this.mHillPoints_Base;
        const offsetPoints = this.mHillPoints_Offset;
        const height = this.mRoomElement.Params.hillHeight;
        const splineType = this.mRoomElement.Params.terrainType;
        if (this.mHillPoints_Offset == null || this.mHillPoints_Base == null ||
            basePoints == null || offsetPoints == null || splineType === 'Flat' ||
            !this.mcanCreateHill) {
            return (null);
        }
        const canRibbon = offsetPoints.length === basePoints.length;
        if (!canRibbon) {
            newError('Unstable calculation', 'expansion failed');
            return (null);
        }
        let topPolygon = [];
        let bottomPolygon = [];
        if (splineType === 'Top') {
            topPolygon = this.mHillPoints_Base;
            bottomPolygon = this.mHillPoints_Offset;
        }
        if (splineType === 'Bottom') {
            topPolygon = this.mHillPoints_Offset;
            bottomPolygon = this.mHillPoints_Base;
        }
        const curve = this.curveFunction(this.mRoomElement.Params.hillProfile);
        const steps = getSteps(curve);
        const inters = steps.map((x, i) => getInterpolatedPolygon(bottomPolygon, topPolygon, x)
            .map((y) => { y.y += curve[i] * height; return y; }));
        const side = CreateRibbon('rectangle_hill_side', {
            pathArray: inters,
            closePath: true,
            sideOrientation: Mesh.BACKSIDE,
            updatable: true
        }, this.mScene);
        let positions = side.getVerticesData(VertexBuffer.PositionKind);
        if (positions != null) {
            let minX = Infinity;
            let minZ = Infinity;
            let maxX = -Infinity;
            let maxZ = -Infinity;
            let x = 0;
            let z = 0;
            let uvs = [];
            for (var i1 = 0; i1 < positions.length / 3; i1++) {
                x = positions[3 * i1];
                z = positions[3 * i1 + 2];
                minX = Math.min(x, minX);
                minZ = Math.min(z, minZ);
                maxX = Math.max(x, maxX);
                maxZ = Math.max(z, maxZ);
                uvs.push(x, z);
            }
            for (let i2 = 0; i2 < uvs.length / 2; i2++) {
                // uvs[2 * i2] = (uvs[2 * i2] - minX) / ((maxX - minX) * 0.15);
                // uvs[2 * i2 + 1] = (uvs[2 * i2 + 1] - minZ) / ((maxZ - minZ) * 0.15);
                uvs[2 * i2] = (uvs[2 * i2] - minX);
                uvs[2 * i2 + 1] = (uvs[2 * i2 + 1] - minZ);
            }
            side.updateVerticesData(VertexBuffer.UVKind, uvs);
        }
        side.parent = pParent;
        side.material = this.mRoomElement.localMaterial;
        side.receiveShadows = true;
        return side;
    }
}
function offset(polygon, spacing) {
    const geoInput = vectorCoordinates2JTS(polygon);
    if (!geoInput[0].equals(geoInput[geoInput.length - 1])) {
        geoInput.push(geoInput[0]);
    }
    const geometryFactory = new jsts.geom.GeometryFactory();
    const ring = geometryFactory.createLineString(geoInput);
    const shell = geometryFactory.createPolygon(ring, []);
    const bufferParameters = new jsts.operation.buffer.BufferParameters();
    bufferParameters.setEndCapStyle(jsts.operation.buffer.BufferParameters.CAP_FLAT);
    bufferParameters.setQuadrantSegments(1);
    bufferParameters.setJoinStyle(jsts.operation.buffer.BufferParameters.JOIN_MITRE);
    const buffered = shell.buffer(spacing, bufferParameters);
    const simplified = simplify();
    const points = simplified.getCoordinates();
    if (points.length === 0) {
        return [];
    }
    if (points[0].equals(points[points.length - 1])) {
        points.pop();
    }
    return points.map((x) => new Vector3(x.x, 0, x.y));
    function simplify() {
        let a;
        let b = buffered;
        let tol = 0.01;
        const targetVertexCount = polygon.length;
        let tNumPoint = b.getNumPoints();
        while (targetVertexCount + 1 < tNumPoint) {
            a = b;
            b = jsts.simplify.TopologyPreservingSimplifier.simplify(b, tol);
            tNumPoint = b.getNumPoints();
            if (a.getNumPoints() === tNumPoint) {
                tol *= 2;
                if (tol > 50) {
                    break;
                }
            }
        }
        return b;
    }
}
function vectorCoordinates2JTS(tPoints) {
    return tPoints.map((point) => new jsts.geom.Coordinate(point.x, point.z));
}
function getSteps(points) {
    const step = 1 / (points.length - 1);
    const res = [];
    for (let i = 0; i < points.length; i++) {
        res.push(i * step);
    }
    return res;
}
function getInterpolatedPolygon(a, b, n) {
    const res = [];
    for (let i = 0; i < a.length; i++) {
        res.push(b[i].subtract(a[i]).scale(n).add(a[i]));
    }
    return res;
}
